<template>
  <div
    :class="itemClasses"
    :data-test-id="$testId('detailsAsideStatusMenuTitle')"
  >
    <span
      v-if="!menuSections"
      :class="$style.label"
    >
      {{ label }}
    </span>
    <DropDownMenu
      v-if="menuSections"
      :classNames="dropDownMenuClassNames"
      :disabled="disabled"
      :isLargeDropDown="false"
      :menuSections="menuSections"
      :menuTitle="label"
    />
    <div
      v-if="subtext"
      :class="[$style.subtext, statusSubtextColor]"
    >
      <MarkdownInline
        :mdTemplate="subtext"
      />
    </div>
  </div>
</template>

<script>
import { MarkdownInline } from '@jumpcloud/ui-components';
import { StatusColorNames } from 'client/util/Constants';
import { mixin as clickaway } from 'vue-clickaway';
import DropDownMenu from 'components/DropDownMenu/DropDownMenu.vue';

/**
 * This component generates a list of statuses. Statuses may also have actions which are displayed
 * in a dropdown. Example status array:
 *  [
      {
        label: 'password pending',
       },
       {
         color: 'warning',
         label: 'in Mfa Enrollment',
         subtext: 'expires 03-24-2018',
         menuSections: [
           {
             menuItems: [
               { label: 'Action 1', action: this.action },
               { label: 'Action 2', action: this.action },
             ],
           },
         ],
       },
    ]
 *  See the methods in order to see how to further customize colors
 */
export default {
  name: 'Status',

  components: {
    DropDownMenu,
    MarkdownInline,
  },

  mixins: [clickaway],

  props: {
    color: {
      type: String,
      default: StatusColorNames.active,
    },
    disabled: {
      default: false,
      type: Boolean,
    },
    label: {
      type: String,
      required: true,
    },
    menuSections: {
      type: Array,
      default: undefined,
    },
    subtext: {
      type: String,
      default: '',
    },
    subtextColor: {
      type: String,
      default: '',
    },
  },

  computed: {
    itemClasses() {
      const { $style } = this;
      const classes = [$style.item, this.statusColor];
      classes.push(this.menuSections ? $style.dropdownItem : $style.defaultItem);
      return classes;
    },
    /**
     * Gets the color of the status color. defaults to green if passed an unknown color.
     */
    statusColor() {
      const { $style } = this;
      return this.colorMap[this.color] || $style.green;
    },

    /**
     * Default color of the subtext matches the color of the status. To specify a different
     * color other than the status, put a subtextColor in the status object.
     */
    statusSubtextColor() {
      return this.colorMap[this.subtextColor] || this.statusColor;
    },
  },

  created() {
    const { $style } = this;
    this.dropDownMenuClassNames = {
      container: 'dropDownContainerNoFlexEnd',
      dropDownList: 'statusDropDownList',
      dropDownMenuTitle: 'detailsAsideStatusMenuTitle',
    };
    // List of current possible colors for the status
    this.colorMap = {
      [StatusColorNames.active]: $style.green,
      [StatusColorNames.critical]: $style.red,
      [StatusColorNames.info]: $style.blue,
      [StatusColorNames.inactive]: $style.gray,
      [StatusColorNames.warning]: $style.orange,
    };
  },
};
</script>

<style module>
@value (
  jcGreen,
  jcOrange,
  jcRed,
  gradientDark2,
  gradientDark3,
  greyLight4
) from '~css/colors.css';

.label {
  color: gradientDark2;
  font-size: 1.1rem;
  font-weight: 600;
  vertical-align: middle;
}

.item {
  line-height: 1.5;
  margin-top: 7px;
  position: relative;
}

.item::before {
  content: '\2022';
  font-size: 32px;
  left: -24px;
  position: absolute;
  top: -13px;
}

.defaultItem::before {
  top: -15px;
}

.dropdownItem::before {
  top: -15px;
}

.blue {
  color: gradientDark3;
}

.gray {
  color: greyLight4;
}

.green {
  color: jcGreen;
}

.orange {
  color: jcOrange;
}

.red {
  color: jcRed;
}

.subtext {
  font-size: 11px;
  font-weight: 0.86rem;
}
</style>
